import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Container,
  useTheme,
  Alert,
  Snackbar,
  AppBar,
  Toolbar,
  Button,
  IconButton,
} from '@mui/material';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import STLManagement from '../components/AdminPanel/STLManagement';
import PortfolioManagement from '../components/AdminPanel/PortfolioManagement';
import CostEstimationSettings from '../components/AdminPanel/CostEstimationSettings';
import CredentialManagement from '../components/AdminPanel/CredentialManagement';
import CategoryManagement from '../components/AdminPanel/CategoryManagement';
import AdminLogin from '../components/AdminPanel/AdminLogin';
import { Logout, Security } from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Admin: React.FC = () => {
  const theme = useTheme();
  const { t } = useLanguage();
  const { isAuthenticated, logout } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const handleLogout = () => {
    logout();
    showNotification('Logged out successfully', 'info');
  };

  if (!isAuthenticated) {
    return <AdminLogin />;
  }

  return (
    <Box>
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            CraftiCore Admin Panel
          </Typography>
          <IconButton color="inherit" onClick={() => setTabValue(4)}>
            <Security />
          </IconButton>
          <Button color="inherit" onClick={handleLogout} startIcon={<Logout />}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.secondary.light}15 100%)`,
            borderRadius: 4,
            mb: 4,
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            color="primary"
            sx={{ fontWeight: 700 }}
          >
            Admin Panel
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Manage your CraftiCore content, media, and settings
          </Typography>
        </Paper>

        <Paper elevation={0} sx={{ borderRadius: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="admin tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Portfolio" />
              <Tab label="STL Models" />
              <Tab label="Categories" />
              <Tab label="Cost Settings" />
              <Tab label="Security" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <PortfolioManagement onNotification={showNotification} />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <STLManagement onNotification={showNotification} />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <CategoryManagement onNotification={showNotification} />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <CostEstimationSettings onNotification={showNotification} />
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <CredentialManagement onNotification={showNotification} />
          </TabPanel>
        </Paper>

        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Admin;